import React, { useContext, useState } from 'react';
import { StateContext } from '../../StateContext';
import { useLanguage } from '../../Services/LanguageContext';
import { MechanicTypes, getMechanicTypeLabel } from '../../Services/types';
import PrintableYearOverviewPage from '../../General/PrintablePages/PrintableYearOverviewPage';
import { totalHours } from '../../General/Links/weekView';

const formatHours = (hours: number): string => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}u ${m}m`;
};

interface StatCardProps {
    label: string;
    value: string | number;
    purple?: boolean;
}

const StatCard = ({ label, value, purple = false }: StatCardProps) => (
    <div className={`stat-card${purple ? ' purple' : ''}`}>
        <div className='stat-card-label'>{label}</div>
        <div className='stat-card-value'>{value}</div>
    </div>
);

const StatsOverview = () => {
    const { loginData } = useContext(StateContext);
    const { t, language } = useLanguage();
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    const projects = loginData?.projects ?? [];
    const mechanics = loginData?.mechanics ?? [];

    const totalLinks = projects.reduce((sum, p) => sum + (p.links?.length ?? 0), 0);

    const totalHoursWorked = projects.reduce(
        (sum, p) =>
            sum +
            (p.links ?? []).reduce((lSum, l) => lSum + totalHours(l.weekData ?? []), 0),
        0
    );

    const totalHoursEstimated = projects.reduce((sum, p) => {
        const hasTypes = (p.types ?? []).length > 0;
        return sum + (hasTypes
            ? (p.types ?? []).reduce((s, pt) => s + pt.calculatedTimeInSeconds / 3600, 0)
            : (p.estimatedTimeInSeconds ?? 0) / 3600);
    }, 0);

    const materialsDelivered = projects.filter(p => p.materialsDelivered).length;

    const workTypeCounts: Record<string, number> = {};
    projects.forEach(p => (p.types ?? []).forEach(pt => {
        workTypeCounts[pt.name] = (workTypeCounts[pt.name] ?? 0) + 1;
    }));
    const workTypeEntries = Object.keys(workTypeCounts)
        .map(name => ({ name, count: workTypeCounts[name] }))
        .sort((a, b) => b.count - a.count);

    return (
        <div>
            <div className='stats-grid'>
                <StatCard label={t('totalProjects')} value={projects.length} />
                <StatCard label={t('totalMechanics')} value={mechanics.length} purple />
                <StatCard label={t('totalConnections')} value={totalLinks} />
                <StatCard label={t('totalHoursWorked')} value={formatHours(totalHoursWorked)} purple />
                <StatCard label={t('totalHoursEstimated')} value={formatHours(totalHoursEstimated)} />
                <StatCard label={t('materialsDeliveredRatio')} value={`${materialsDelivered}/${projects.length}`} purple />
            </div>

            <div className='stats-breakdowns'>
                <div className='breakdown-card'>
                    <h3>{t('mechanicsByType')}</h3>
                    {MechanicTypes.map(({ value }) => (
                        <div key={value} className='breakdown-row'>
                            <span>{getMechanicTypeLabel(value, language)}</span>
                            <span className='breakdown-count'>
                                {mechanics.filter(m => m.type === value).length}
                            </span>
                        </div>
                    ))}
                </div>

                <div className='breakdown-card'>
                    <h3>{t('projectsByType')}</h3>
                    {workTypeEntries.map(({ name, count }) => (
                        <div key={name} className='breakdown-row'>
                            <span>{name}</span>
                            <span className='breakdown-count'>{count}</span>
                        </div>
                    ))}
                </div>
            </div>

            <div className='year-print-row'>
                <h3>{t('year')}</h3>
                <input
                    type='number'
                    value={selectedYear}
                    onChange={e =>
                        setSelectedYear(parseInt(e.target.value) || new Date().getFullYear())
                    }
                    className='year-input'
                    min={2020}
                    max={2099}
                />
                <PrintableYearOverviewPage year={selectedYear} />
            </div>
        </div>
    );
};

export default StatsOverview;
